import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

Vue.use(Vuex)

// ---------------------------------------------------------------------------
// Store metadata — single source of truth for IDs, names, colours, abbrs.
// HomeView & BrowserView import this to drive their templates dynamically,
// eliminating the previous 20-block v-if repetition.
// ---------------------------------------------------------------------------
export const STORES_META = [
  { id: 1,  key: 'steam',         name: 'Steam',            color: '#1b9dd9', abbr: 'ST' },
  { id: 2,  key: 'gamerGate',     name: 'GamersGate',       color: '#e8a020', abbr: 'GG' },
  { id: 3,  key: 'greenManGaming',name: 'GreenManGaming',   color: '#22aa55', abbr: 'GM' },
  { id: 6,  key: 'direct2Drive',  name: 'Direct2Drive',     color: '#e05c20', abbr: 'D2' },
  { id: 7,  key: 'gog',           name: 'GOG',              color: '#a84af9', abbr: 'GO' },
  { id: 8,  key: 'origin',        name: 'Origin',           color: '#f56c2d', abbr: 'OR' },
  { id: 11, key: 'humblebundle',  name: 'Humble Store',     color: '#cc3333', abbr: 'HB' },
  { id: 13, key: 'ubisoft',       name: 'Uplay',            color: '#0070f0', abbr: 'UB' },
  { id: 15, key: 'fanatical',     name: 'Fanatical',        color: '#e85d26', abbr: 'FA' },
  { id: 21, key: 'wingamestore',  name: 'WinGameStore',     color: '#2c7bb6', abbr: 'WG' },
  { id: 23, key: 'gameBillet',    name: 'GameBillet',       color: '#c0392b', abbr: 'GB' },
  { id: 24, key: 'voidu',         name: 'Voidu',            color: '#8e44ad', abbr: 'VO' },
  { id: 25, key: 'epicGameStore', name: 'Epic Games Store', color: '#3a3a3a', abbr: 'EG' },
  { id: 27, key: 'gamePlanet',    name: 'Gamesplanet',      color: '#1a7f37', abbr: 'GP' },
  { id: 28, key: 'gamesload',     name: 'Gamesload',        color: '#2980b9', abbr: 'GL' },
  { id: 29, key: 'twoGames',      name: '2Game',            color: '#d35400', abbr: '2G' },
  { id: 30, key: 'indieGala',     name: 'IndieGala',        color: '#16a085', abbr: 'IG' },
  { id: 31, key: 'blizzardShop',  name: 'Blizzard Shop',    color: '#00aeff', abbr: 'BZ' },
  { id: 32, key: 'allYouPlay',    name: 'AllYouPlay',       color: '#27ae60', abbr: 'AY' },
  { id: 33, key: 'dlGamer',       name: 'DLGamer',          color: '#e74c3c', abbr: 'DL' },
  { id: 34, key: 'noctre',        name: 'Noctre',           color: '#6c3483', abbr: 'NC' },
]

// Build initial per-store state from the metadata array
const perStoreState = STORES_META.reduce((acc, s) => {
  acc[s.key] = []
  return acc
}, {})

// Build getters (top-5 slices) from the metadata array — fixed: slice not splice
const perStoreGetters = STORES_META.reduce((acc, s) => {
  acc[`${s.key}Deals`] = (state) => state[s.key].slice(0, 5)
  return acc
}, {})

// Build mutations from the metadata array
const perStoreMutations = STORES_META.reduce((acc, s) => {
  acc[s.key] = (state, data) => { state[s.key] = data }
  return acc
}, {})

// ---------------------------------------------------------------------------

const state = {
  listGames:      [],
  listDeals:      [],
  listStores:     [],
  wishlist:       [],
  wishlistAlerts: {},
  alertConfig:    { action: null, email: null, gameID: null, price: 0 },
  currentUser:    null,
  isAuthLoading:  true,
  ...perStoreState,
}

const getters = {
  listDeals:        (state) => state.listDeals,
  isLoggedIn:       (state) => !!state.currentUser,
  isAnonymousUser:  (state) => state.currentUser?.is_anonymous === true,
  isGoogleUser:     (state) => {
    if (!state.currentUser) return false
    return (state.currentUser.identities || []).some(i => i.provider === 'google')
  },
  currentUserEmail: (state) => state.currentUser?.email ?? null,
  ...perStoreGetters,
}

const mutations = {
  listGames  (state, data) { state.listGames  = data },
  listStores (state, data) { state.listStores = data },
  listDeals  (state, data) { state.listDeals  = data },

  setAlertConfig  (state, data) { state.alertConfig = { ...data } },
  clearAlertConfig(state)       { state.alertConfig = { action: null, email: null, gameID: null, price: 0 } },

  setWishlistAlert (state, { gameID, email, price, title }) {
    state.wishlistAlerts = { ...state.wishlistAlerts, [gameID]: { email, price, title } }
  },
  clearWishlistAlert (state, gameID) {
    const next = { ...state.wishlistAlerts }
    delete next[gameID]
    state.wishlistAlerts = next
  },

  loadWishlistAlerts (state, alerts) {
    const next = { ...state.wishlistAlerts }
    for (const alert of alerts) {
      if (!next[alert.gameID]) {
        next[alert.gameID] = {
          email: state.alertConfig?.email || '',
          price: alert.targetPrice,
          title: alert.title,
        }
      }
    }
    state.wishlistAlerts = next
  },

  addToWishlist (state, deal) {
    if (!state.wishlist.find(d => d.gameID === deal.gameID)) {
      state.wishlist.push(deal)
    }
  },
  removeFromWishlist (state, gameID) {
    state.wishlist = state.wishlist.filter(d => d.gameID !== gameID)
  },
  listWishlist (state, items) {
    state.wishlist = items
  },

  setUser (state, user) {
    state.currentUser = user
  },
  clearUser (state) {
    state.currentUser = null
  },
  setAuthLoading (state, val) {
    state.isAuthLoading = val
  },

  ...perStoreMutations,
}

export default new Vuex.Store({
  state,
  getters,
  mutations,
  plugins: [createPersistedState({
    paths: ['wishlist', 'wishlistAlerts', 'alertConfig', 'listStores', ...STORES_META.map(s => s.key)],
  })],
})
